/* eslint-disable @typescript-eslint/naming-convention -- Keepping legacy naming convention for TS conversion */
import type { CSSProperties } from "react";
import { useEffect, useRef, useState } from "react";
import type { Options } from "@popperjs/core";
import classnames from "classnames";
import { has, trim, uniqBy } from "lodash";
import { createPortal } from "react-dom";
import { usePopper } from "react-popper";
import scrollIntoView from "scroll-into-view-if-needed";
import { useDetectEscapeKeyPress } from "@circle-react/hooks/useDetectEscapeKeyPress";
import { useDropdown } from "@circle-react-shared/DropdownMenu";
import { useFilteredItems } from "@circle-react-uikit/Select/useFilteredItems";
import { InputContainer } from "./InputContainer";
import { MultiSelectInput } from "./MultiSelectInput";
import { OptionsDropdown } from "./OptionsDropdown";
import { SingleSelectInput } from "./SingleSelectInput";
import { ValueAsHiddenFields } from "./ValueAsHiddenFields";
import { useInputController } from "./useInputController";
import "./styles.scss";

export interface SelectProps {
  addable?: boolean | ((value: string) => any);
  appendTo?: HTMLElement | null;
  autoClearSearchValue?: boolean;
  autoClose?: boolean | null;
  autoFocus?: boolean;
  data?: any;
  defaultValue?: any;
  direction?: "down" | "up";
  disabled?: boolean;
  filterBy?: (item: any, searchValue: string) => boolean;
  hideIcon?: boolean;
  hideSelectedItems?: boolean;
  inputContainerClassName?: string;
  isLoading?: boolean;
  minSearchValueLength?: number;
  name?: string;
  notFoundContent?: string;
  onChange?: (event: any) => void;
  onSearch?: (searchValue: string) => Promise<void> | void;
  placeholder?: string;
  popperOptions?: Partial<Options>;
  shouldFilterSelectedItems?: boolean;
  showSearchIcon?: boolean;
  useEditIcon?: boolean;
  value?: any;
  variant?: "single" | "tags";
}

export const Select = (props: SelectProps) => {
  const {
    appendTo,
    autoClearSearchValue = true,
    data,
    direction = "down",
    disabled = false,
    hideIcon = false,
    hideSelectedItems = false,
    shouldFilterSelectedItems = false,
    filterBy,
    showSearchIcon = false,
    minSearchValueLength = 0,
    name,
    notFoundContent = "No matches found.",
    inputContainerClassName,
    onSearch,
    placeholder,
    popperOptions = {},
    variant,
    autoFocus,
    isLoading,
  } = props;
  let { autoClose, addable } = props;
  const multiple = variant === "tags";
  if (autoClose === null || autoClose === undefined) {
    autoClose = !multiple;
  }
  const [isLocalLoading, setIsLocalLoading] = useState(isLoading);
  const searchInputRef = useRef<any>();
  const [searchValue, setSearchValue] = useState("");
  const { value, onChange, isUncontrolledInput } = useInputController(props);
  const selectedItemsCacheRef = useRef(
    data.filter((dataItem: any) => value.includes(dataItem.value)),
  );
  const { open, handleClose, toggleDropdown, handleOpen } = useDropdown();
  const isControlledLoading = has(props, "isLoading");

  useEffect(() => {
    if (isControlledLoading) {
      setIsLocalLoading(isLoading);
    }
  }, [isControlledLoading, isLoading, setIsLocalLoading]);

  const popperElRef = useRef<any>(null);
  const popperTargetRef = useRef<any>(null);
  const { styles, attributes } = usePopper(
    popperTargetRef.current,
    popperElRef.current,
    {
      placement: direction === "up" ? "top-start" : "bottom-start",
      ...popperOptions,
    },
  );

  const shouldUsePortal = Boolean(appendTo);
  const dropdownWidth = popperTargetRef.current?.offsetWidth || 500;
  const dropdownStyles: CSSProperties = shouldUsePortal
    ? { ...styles.popper, width: dropdownWidth }
    : {};
  const dropdownAttributes = shouldUsePortal ? attributes.popper : {};
  const showDropdown = open && searchValue.length >= minSearchValueLength;

  useDetectEscapeKeyPress(() => {
    void onSearchChange("");
  });

  if (addable === true) {
    addable = value => ({
      text: value,
      value: value,
      listItemText: `Add ${value}`,
    });
  }

  const onSelect = (item: any) => {
    if (multiple) {
      onChange?.({ value: [...value, item.value] });
      selectedItemsCacheRef.current = [...selectedItemsCacheRef.current, item];
      scrollIntoView(searchInputRef.current, {
        scrollMode: "if-needed",
        block: "nearest",
        inline: "nearest",
      });
    } else {
      selectedItemsCacheRef.current = [item];
      onChange?.({ value: item.value });
    }
    autoClose ? handleClose() : searchInputRef.current.focus();
    autoClearSearchValue && void onSearchChange("");
  };

  // only needed for multiple
  const onDeselect = (item: any) => {
    const newValue = value.filter((v: any) => v !== item.value);
    onChange?.({ value: newValue });
    selectedItemsCacheRef.current = selectedItemsCacheRef.current.filter(
      (i: any) => i.value !== item.value,
    );
  };

  const onSearchChange = async (searchValue: string) => {
    setSearchValue(searchValue);
    !isControlledLoading && setIsLocalLoading(true);
    await onSearch?.(searchValue);
    !isControlledLoading && setIsLocalLoading(false);
  };

  useEffect(() => {
    open && searchInputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        !popperElRef.current?.contains(event.target) &&
        !popperTargetRef.current?.contains(event.target)
      ) {
        handleClose();
      }
    };
    if (open) {
      document.addEventListener("click", handleOutsideClick);
    }
    return () => {
      if (open) {
        document.removeEventListener("click", handleOutsideClick);
      }
    };
  }, [open, popperElRef, popperTargetRef, handleClose]);

  let listItems = useFilteredItems(
    data,
    filterBy,
    searchValue,
    shouldFilterSelectedItems,
    value,
  );

  if (addable && !listItems.length && trim(searchValue)) {
    const addableItem = addable(trim(searchValue));
    if (addableItem) {
      listItems = [addableItem];
    }
  }

  const selectedItems = uniqBy(
    [...selectedItemsCacheRef.current, ...data],
    "value",
  ).filter(dataItem => value.includes(dataItem.value));

  const renderList = (
    <div ref={popperElRef}>
      {showDropdown && (
        <OptionsDropdown
          direction={direction}
          items={listItems}
          onSelect={onSelect}
          loading={isLocalLoading}
          notFoundContent={notFoundContent}
          style={dropdownStyles}
          {...dropdownAttributes}
        />
      )}
    </div>
  );

  const SelectInput = multiple ? MultiSelectInput : SingleSelectInput;
  // minSearchValueLength > 0 that means dropdown will not be visible on click, in those cases we want to use editIcon vs cheveron
  const useEditIcon = props.useEditIcon || minSearchValueLength >= 1;

  return (
    <div className={classnames("react-select", { "!z-10": showDropdown })}>
      {showDropdown && !shouldUsePortal && direction === "up" && renderList}
      {isUncontrolledInput() && (
        <ValueAsHiddenFields value={value} multiple={multiple} name={name} />
      )}
      <InputContainer
        ref={popperTargetRef}
        showDropdown={showDropdown}
        toggleDropdown={toggleDropdown}
        useEditIcon={useEditIcon}
        hideIcon={hideIcon}
        value={value}
        disabled={disabled}
        showSearchIcon={showSearchIcon}
        inputContainerClassName={inputContainerClassName}
      >
        <SelectInput
          searchInputRef={searchInputRef}
          searchValue={searchValue}
          open={open}
          selectedItems={selectedItems}
          onSearchChange={onSearchChange}
          handleOpen={handleOpen}
          placeholder={placeholder}
          onDeselect={onDeselect}
          disabled={disabled}
          hideSelectedItems={hideSelectedItems}
          autoFocus={autoFocus}
        />
      </InputContainer>
      {showDropdown && !shouldUsePortal && direction === "down" && renderList}
      {shouldUsePortal && appendTo && createPortal(renderList, appendTo)}
    </div>
  );
};
